import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import * as moment from 'moment';
import { Alert } from '../../../../../../models/alerts/dto/alert';
import { AlertTypeEnum } from '../../../../../../models/alerts/enum/alert-type.enum';
import { AlertBackgroundColorEnum } from '../../../../../../models/alerts/enum/alert-background-color.enum';
import { PortalTypeIdEnum } from '../../../../../../models/enum/shared/portal-type-id.enum';
import { isoDateStringValidator } from '../../../../../../validators/iso-date-string-validator';

export const MAX_ALERT_TITLE_LENGTH = 80;
export const MAX_ALERT_DESCRIPTION_LENGTH = 200;
export const MAX_ALERT_BUTTON_LENGTH = 40;

export type AlertFormGroup = FormGroup<{
  typeId: FormControl<AlertTypeEnum>;
  title: FormControl<string>;
  description: FormControl<string>;
  includeButton: FormControl<boolean>;
  buttonText: FormControl<string | null>;
  buttonLink: FormControl<string | null>;
  dismissable: FormControl<boolean>;
  colourId: FormControl<number>;
  startDate: FormControl<string | null>;
  startTime: FormControl<string>;
  endDate: FormControl<string | null>;
  endTime: FormControl<string>;
  portalTypeId: FormControl<PortalTypeIdEnum>;
}>;

export function buildAlertFormGroup(alert: Alert | null): AlertFormGroup {
  const isCard = (alert?.typeId ?? AlertTypeEnum.Card) === AlertTypeEnum.Card;
  const hasButton = !!alert?.buttonText || !!alert?.buttonLink;
  const includeButton = !alert?.id ? true : hasButton;

  return new FormGroup(
    {
      typeId: new FormControl<AlertTypeEnum>(alert?.typeId ?? AlertTypeEnum.Card, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      title: new FormControl<string>(alert?.title ?? '', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(MAX_ALERT_TITLE_LENGTH)]
      }),
      description: new FormControl<string>(alert?.description ?? '', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(MAX_ALERT_DESCRIPTION_LENGTH)]
      }),
      includeButton: new FormControl<boolean>(includeButton, { nonNullable: true }),
      buttonText: new FormControl<string | null>(alert?.buttonText ?? null, {
        validators: buildButtonTextValidators(isCard && includeButton)
      }),
      buttonLink: new FormControl<string | null>(alert?.buttonLink ?? null, {
        validators: isCard && includeButton ? [Validators.required] : []
      }),
      dismissable: new FormControl<boolean>(alert?.dismissable ?? false, { nonNullable: true }),
      colourId: new FormControl<number>(alert?.colourId ?? AlertBackgroundColorEnum.CardGreen, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      startDate: new FormControl<string | null>(toUtcStringFromAlertDateString(alert?.startDateString), {
        validators: [Validators.required, isoDateStringValidator]
      }),
      startTime: new FormControl<string>(alert?.startTime ?? '', {
        nonNullable: true,
        validators: [Validators.required]
      }),
      endDate: new FormControl<string | null>(toUtcStringFromAlertDateString(alert?.endDateString), {
        validators: [Validators.required, isoDateStringValidator]
      }),
      endTime: new FormControl<string>(alert?.endTime ?? '', {
        nonNullable: true,
        validators: [Validators.required]
      }),
      portalTypeId: new FormControl<PortalTypeIdEnum>(alert?.portalTypeId ?? PortalTypeIdEnum.Member, {
        nonNullable: true,
        validators: [Validators.required]
      })
    },
    { validators: [startEndDateRangeValidator] }
  );
}

export function buildButtonTextValidators(requireButton: boolean) {
  const validators = [Validators.maxLength(MAX_ALERT_BUTTON_LENGTH)];
  if (requireButton) validators.push(Validators.required);
  return validators;
}

export const START_END_DATE_RANGE_ERROR_KEY = 'startEndDateRangeInvalid';

function startEndDateRangeValidator(group: AbstractControl): ValidationErrors | null {
  const startDate = group.get('startDate')?.value as string | null;
  const startTime = (group.get('startTime')?.value as string | null) ?? '';
  const endDate = group.get('endDate')?.value as string | null;
  const endTime = (group.get('endTime')?.value as string | null) ?? '';
  if (!startDate || !endDate) return null;
  const start = combineDateTime(startDate, startTime);
  const end = combineDateTime(endDate, endTime);
  if (!start.isValid() || !end.isValid()) return null;
  return end.isAfter(start) ? null : { [START_END_DATE_RANGE_ERROR_KEY]: true };
}

function combineDateTime(utcString: string, time: string): moment.Moment {
  const datePart = moment.utc(utcString).format('YYYY-MM-DD');
  return moment(`${datePart} ${time || '00:00'}`, 'YYYY-MM-DD HH:mm');
}

function toUtcStringFromAlertDateString(dateString: string | undefined): string | null {
  if (!dateString) return null;
  const parsed = moment(dateString, 'DD-MM-YYYY', true);
  if (!parsed.isValid()) return null;
  return parsed.format('YYYY-MM-DD');
}

export function utcStringToBackendDateString(utcString: string | null | undefined): string {
  if (!utcString) return '';
  return moment.utc(utcString).format('YYYY-MM-DD');
}

export function applyFormToAlert(form: AlertFormGroup, base: Alert): Alert {
  const value = form.getRawValue();
  const next = Object.assign(new Alert(), base);
  next.typeId = value.typeId;
  next.title = value.title;
  next.description = value.description;
  next.colourId = value.colourId;
  next.portalTypeId = value.portalTypeId;
  if (value.typeId === AlertTypeEnum.Card) {
    next.buttonText = value.includeButton ? value.buttonText : null;
    next.buttonLink = value.includeButton ? value.buttonLink : null;
    next.dismissable = false;
  } else {
    next.buttonText = null;
    next.buttonLink = null;
    next.dismissable = value.dismissable;
  }
  next.startDateString = utcStringToBackendDateString(value.startDate);
  next.endDateString = utcStringToBackendDateString(value.endDate);
  next.startTime = value.startTime;
  next.endTime = value.endTime;
  return next;
}
